import {
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  BlinkingDot,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Backdrop,
  LinearProgressWithLabel,
} from '@common/components'
import { CenterContainer } from '@common/containers'
import { useNavigate, useParams } from '@lib/router'
import React, { useEffect, useRef, useState } from 'react'
import { styled } from '@mui/material/styles'
import imgIELTS from '@assets/images/pengguin-avatars4.png'
import imgJobInterview from '@assets/images/pengguin-avatars8.png'
import imgConversation from '@assets/images/pengguin-avatars7.png'
import { CircularProgress, CloseIcon, FiberManualRecordIcon, PauseIcon } from '@common/icons'
import AudioVisualizer from '../audio-visualizer/audio-visualizer'
import useRecording from '@features/(unsw)/hooks/use-recording'
import { useAppSelector, useToggle } from '@common/hooks'
import { useTheme } from '@features/theme/theme.hooks'
import CheckBrowserCompat from '@features/recordings/components/add-recording/check-browser-compat'
import AllowMicModal from '@features/recordings/components/add-recording/allow-mic-modal'
import CheckMicPermission from '@features/recordings/utils/mic-permission'
import isSupportedBrowser from '@common/utils/supported-browser'
import { InterviewQuestionView, InterviewQuestionViewHandle } from './interview-question-view'
import { useLazyRevisionGetQuestionsQuery } from '@app/services/auto-generated/revision'
import { RECORDINGS_ROUTE } from '@common/constants/routes'
import CompletedInterviewModal from '../completed-interview-modal/completed-interview-modal'

const StepCircle = styled('div')<{ active?: boolean }>(({ active }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: '#1976d2',
  opacity: active ? 1 : 0.3,
  transition: 'opacity 0.3s ease',
}))

const BubbleView = () => {
  const navigate = useNavigate()
  const [isMicTested, setIsMicTested] = useState(false)
  const { theme } = useTheme()
  const [micStatus, setMicStatus] = React.useState('')
  const { recordingStatus } = useAppSelector((state) => state.recordings)
  const { workshop } = useParams()
  const {
    open: openAllowMic,
    handleClose: handleCloseAllowMic,
    handleOpen: handleOpenAllowMic,
  } = useToggle(false)
  const {
    open: openBrowserCompat,
    handleClose: handleCloseBrowserCompat,
    handleOpen: handleOpenBrowserCompat,
  } = useToggle(false)
  const [isLoading, setIsLoading] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [isCompleted, setIsCompleted] = useState(false)
  const [currentAiQuestion, setCurrentAiQuestion] = useState('')
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [selectedDevice, setSelectedDevice] = useState<string | undefined>(undefined)
  const animationFrameRef = useRef<number>()
  const questionRef = useRef<InterviewQuestionViewHandle>(null)
  const getWorkshopImage = () => {
    switch (workshop) {
      case 'job-interview':
        return imgJobInterview
      case 'ielts':
        return imgIELTS
      default:
        return imgConversation
    }
  }
  const {
    isSuccess: isSuccessUpload,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
    seconds,
    minutes,
    recordingId,
    cleanUp: cleanUpRecording,
    ffmpegLoaded,
    stream: streamRecord,
    loadingProgress,
  } = useRecording(true, selectedDevice)

  const [getQuestions, { data: questionsData, isLoading: isLoadingQuestions }] =
    useLazyRevisionGetQuestionsQuery()
  const recordingInit = async () => {
    if (!isSupportedBrowser()) {
      handleOpenBrowserCompat()
      return
    }
    if (!ffmpegLoaded) {
      // Add an alert or message to inform the user that FFmpeg is not loaded
      console.error('FFmpeg is not loaded. Recording is not possible.')
      return
    }
    const micState = await CheckMicPermission()
    setMicStatus(micState)
    if (micState === 'granted') {
      setMicStatus('')
      startRecording()
    } else {
      handleOpenAllowMic()
    }
  }
  const cleanup = () => {
    if (stream) {
      stream.getTracks().forEach((track) => {
        track.stop()
      })
      setStream(null)
    }
    if (streamRecord) {
      streamRecord.getTracks().forEach((track) => {
        track.stop()
      })
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = undefined
    }
    cleanUpRecording()
  }

  useEffect(() => {
    return () => {
      cleanup()
    }
  }, [])

  const handleRefresh = () => {
    questionRef.current?.getNewQuestion()
  }

  const handleNextQuestion = async () => {
    setActiveStep(activeStep + 1)

    if (activeStep <= 2) {
      handleRefresh()
      if (activeStep === 2) {
        const res = await getQuestions({ recordingId: recordingId as string })
        setCurrentAiQuestion(res.data?.questions[0].question || '')
        return
      }
    }

    setCurrentAiQuestion(questionsData?.questions[activeStep - 2].question || '')
  }
  const handleSubmit = async () => {
    if (recordingStatus === false) {
      resumeRecording()
    }
    await stopRecording()
    setIsLoading(true)
    cleanUpRecording()
  }

  useEffect(() => {
    if (isSuccessUpload) {
      setIsLoading(false)
      setIsCompleted(true)
    }
  }, [isSuccessUpload])

  return (
    <>
      <CompletedInterviewModal
        open={isCompleted}
        onClose={() => {
          navigate(RECORDINGS_ROUTE)
          setIsCompleted(false)
        }}
      />
      <Backdrop open={isLoading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress />
      </Backdrop>
      {isMicTested ? (
        <CenterContainer>
          <AllowMicModal micState={micStatus} open={openAllowMic} onClose={handleCloseAllowMic} />
          <CheckBrowserCompat open={openBrowserCompat} onClose={handleCloseBrowserCompat} />
          <Stack spacing={3} alignItems='center'>
            <Stack direction='row' spacing={2} justifyContent='center'>
              {[...Array(6)].map((_, index) => (
                <StepCircle key={index} active={index === activeStep} />
              ))}
            </Stack>
            <img
              src={getWorkshopImage()}
              alt={`${workshop} workshop`}
              style={{ width: 200 }} // Adjust size as needed
            />
            {isLoadingQuestions ? (
              <Typography color='text.secondary'>Generating your next question...</Typography>
            ) : (
              <InterviewQuestionView
                ref={questionRef}
                workshop={workshop}
                question={currentAiQuestion}
              />
            )}
            {/* <Typography>{workshop}</Typography> */}
            <Stack
              direction='row'
              spacing={1}
              justifyContent='center'
              alignItems='center'
              sx={{ border: '1px solid', borderRadius: 10, px: 2, py: 0.5 }}
            >
              <IconButton
                // sx={{ color: 'white' }}
                onClick={() => {
                  recordingStatus ? pauseRecording() : resumeRecording()
                }}
              >
                {recordingStatus ? <PauseIcon /> : <FiberManualRecordIcon />}
              </IconButton>
              <AudioVisualizer
                stream={streamRecord}
                height={40}
                backgroundColor={theme.palette.background.default}
                sx={{ border: 'none' }}
              />
              <Box sx={{ minWidth: 15 }}>
                <BlinkingDot status={recordingStatus ? 'recording' : 'paused'} />
              </Box>
              <Typography sx={{ fontWeight: '500', width: '40px' }}>{`${String(minutes).padStart(
                2,
                '0',
              )}:${String(seconds).padStart(2, '0')}`}</Typography>
            </Stack>
            <Button
              disabled={isLoadingQuestions}
              variant='contained'
              color='primary'
              onClick={activeStep < 5 ? handleNextQuestion : handleSubmit}
            >
              {activeStep < 5 ? 'Next question' : 'Submit'}
            </Button>
          </Stack>
        </CenterContainer>
      ) : (
        <>
          <TestMicrophone
            checkMic={setIsMicTested}
            startRecording={recordingInit}
            stream={stream}
            setStream={setStream}
            changeDevice={setSelectedDevice}
            disabled={!ffmpegLoaded}
          >
            <Box sx={{ mt: 3, width: '150px', mx: 'auto' }}>
              {!ffmpegLoaded && <LinearProgressWithLabel value={loadingProgress} />}
            </Box>
          </TestMicrophone>
        </>
      )}
    </>
  )
}

const TestMicrophone = (props: {
  checkMic: (value: boolean) => void
  startRecording: () => void
  stream: MediaStream | null
  setStream: (stream: MediaStream | null) => void
  changeDevice: (device: string | undefined) => void
  disabled: boolean
  children: React.ReactNode
}) => {
  const { checkMic, startRecording, stream, setStream, changeDevice, disabled, children } = props
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([])
  const [selectedDevice, setSelectedDevice] = useState<string>('')
  const navigate = useNavigate()

  const startMicTest = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: selectedDevice ? { exact: selectedDevice } : undefined },
      })
      setStream(mediaStream)
    } catch (error) {
      console.error('Error accessing microphone:', error)
    }
  }
  useEffect(() => {
    async function initializeMicrophone() {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true })
        const devices = await navigator.mediaDevices.enumerateDevices()
        const audioInputs = devices.filter((device) => device.kind === 'audioinput')
        setAudioDevices(audioInputs)

        if (audioInputs.length > 0) {
          setSelectedDevice(audioInputs[0].deviceId)
          changeDevice(audioInputs[0].deviceId)
        }
      } catch (error) {
        console.error('Error initializing microphone:', error)
      }
    }

    initializeMicrophone()
  }, [])

  useEffect(() => {
    if (selectedDevice) {
      startMicTest()
    }
  }, [selectedDevice])

  const cleanupMicTest = () => {
    if (stream) {
      stream.getTracks().forEach((track) => {
        track.stop()
      })
      setStream(null)
    }
  }

  useEffect(() => {
    return () => {
      cleanupMicTest()
    }
  }, [])

  const handleClose = () => {
    cleanupMicTest()
    navigate(-1)
  }

  const handleStartWorkshop = () => {
    cleanupMicTest()
    checkMic(true)
    startRecording()
  }

  return (
    <CenterContainer>
      <Paper
        elevation={3}
        sx={{ width: '100%', maxWidth: 550, mx: 'auto', p: 4, position: 'relative' }}
      >
        <IconButton onClick={handleClose} sx={{ position: 'absolute', top: 5, right: 5 }}>
          <CloseIcon />
        </IconButton>
        <Typography variant='body1' textAlign='center' gutterBottom fontWeight='bold'>
          Let&apos;s make sure our AI can hear you.
        </Typography>

        <Typography variant='body1' color='text.secondary' textAlign='center' sx={{ mb: 4 }}>
          Read this line aloud and see if you see the line animation.
        </Typography>

        <FormControl fullWidth sx={{ mb: 4 }}>
          <InputLabel>Select Microphone</InputLabel>
          <Select
            value={selectedDevice}
            label='Select Microphone'
            onChange={(e) => {
              setSelectedDevice(e.target.value as string)
              changeDevice(e.target.value as string)
            }}
          >
            {audioDevices.map((device) => (
              <MenuItem key={device.deviceId} value={device.deviceId}>
                {device.label || `Microphone ${device.deviceId.slice(0, 5)}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ mb: 4 }}>
          <AudioVisualizer stream={stream} />
        </Box>
        {children}
        <Button
          disabled={disabled}
          variant='contained'
          onClick={handleStartWorkshop}
          sx={{ mx: 'auto', display: 'block', fontWeight: 'bold', px: 2 }}
        >
          Start Now
        </Button>
      </Paper>
    </CenterContainer>
  )
}

export default BubbleView
